import { capitalizeFirstLetter } from "helpers/commonHelpers";
import {
  dangerousGoodsOptions,
  temperatureControlledOptions,
} from "pages/shipper/RequestForm/components/PackingList/PackingListDefaultOptions";
import { IntlShape } from "react-intl";

const formatDimensions = (length: number, width: number, height: number, unit: string) =>
  `${length} x ${width} x ${height} ${unit}`;

const generateRows = (commodityData: any, invoiceConfig: any, intl: IntlShape) => {
  const {
    actual_additional_information,
    actual_container_type,
    actual_crating_required,
    actual_dangerous_good_value,
    actual_good_customs_information,
    actual_hazardous,
    actual_height,
    actual_length,
    actual_m_unit,
    actual_nmfc_class,
    actual_parent_id,
    actual_stackable,
    actual_temperature_control_required,
    actual_temperature_control_value,
    actual_units,
    actual_w_unit,
    actual_width,
    actual_handling_unit,
    actual_weight,
    requested_additional_information,
    requested_container_type,
    requested_crating_required,
    requested_dangerous_good_value,
    requested_good_customs_information,
    requested_handling_unit,
    requested_height,
    requested_hazardous,
    requested_length,
    requested_m_unit,
    requested_nmfc_class,
    requested_stackable,
    requested_temperature_control_required,
    requested_temperature_control_value,
    requested_units,
    requested_width,
    requested_weight,
    requested_w_unit,
  } = commodityData;

  const handlingUnitConfig = invoiceConfig.handling_unit_options.find(
    (option: any) => option.label === commodityData.actual_handling_unit,
  );

  const defaultRows = [
    {
      name: intl.formatMessage({ id: "new-request.packing-list.handlingUnit", defaultMessage: "Handling Unit" }),
      shouldRender: true,
      formatFn: (value: any) => capitalizeFirstLetter(value),
      requestedValue: requested_handling_unit,
      actualValue: actual_handling_unit,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.containerType", defaultMessage: "Container Type" }),
      shouldRender: handlingUnitConfig?.label === "container",
      formatFn: (value: any) => value,
      requestedValue: requested_container_type,
      actualValue: actual_container_type,
    },
    {
      name: intl.formatMessage({ id: "general.size", defaultMessage: "Size(LxWxH)" }),
      shouldRender: handlingUnitConfig?.l_w_h !== "off" && handlingUnitConfig?.label !== "container",
      formatFn: (value: any) => value,
      requestedValue: formatDimensions(requested_length, requested_width, requested_height, requested_m_unit),
      actualValue: formatDimensions(actual_length, actual_width, actual_height, actual_m_unit),
    },
    {
      name: intl.formatMessage({ id: "general.units", defaultMessage: "Units" }),
      shouldRender: handlingUnitConfig?.quantity !== "off",
      formatFn: (value: any) => value,
      requestedValue: requested_units,
      actualValue: actual_units,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.unitWeight", defaultMessage: "Unit weight" }),
      shouldRender: handlingUnitConfig?.weight !== "off",
      formatFn: (value: any) => value,
      requestedValue: `${requested_weight} ${requested_w_unit}`,
      actualValue: `${actual_weight} ${actual_w_unit}`,
    },
    {
      name: intl.formatMessage({ id: "general.totalWeight", defaultMessage: "Total weight" }),
      shouldRender: handlingUnitConfig?.weight !== "off",
      formatFn: (value: any) => value,
      requestedValue: `${requested_weight * requested_units} ${requested_w_unit}`,
      actualValue: `${actual_weight * actual_units} ${actual_w_unit}`,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.hazardous", defaultMessage: "Hazardous" }),
      shouldRender: requested_hazardous || actual_hazardous,
      formatFn: (value: any) => (value === "-" ? "-" : value ? "Yes" : "No"),
      requestedValue: requested_hazardous,
      actualValue: actual_hazardous,
    },
    {
      name: intl.formatMessage({ id: "general.dangerous", defaultMessage: "Dangerous" }),
      shouldRender: requested_dangerous_good_value || actual_dangerous_good_value,
      formatFn: (value: any) =>
        value === "-"
          ? "-"
          : value
          ? `Yes (${dangerousGoodsOptions(intl).find((option) => option.value === value)?.label})`
          : "No",
      requestedValue: requested_dangerous_good_value,
      actualValue: actual_dangerous_good_value,
    },
    {
      name: intl.formatMessage({ id: "general.temperatureControl", defaultMessage: "Temperature control" }),
      shouldRender: requested_temperature_control_required || actual_temperature_control_required,
      formatFn: (value: any) =>
        typeof value === "number" || !isNaN(parseFloat(value)) // Sometimes we receive a number instead of a string or a number as a string
          ? temperatureControlledOptions(intl).find((option) => option.value === value.toString())?.label || "-"
          : value,
      requestedValue: requested_temperature_control_value,
      actualValue: actual_temperature_control_value,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.stackable", defaultMessage: "Stackable" }),
      shouldRender: requested_stackable || actual_stackable,
      formatFn: (value: any) => (value === "-" ? "-" : value ? "Yes" : "No"),
      requestedValue: requested_stackable,
      actualValue: actual_stackable,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.cratingRequired", defaultMessage: "Crating required" }),
      shouldRender: requested_crating_required || actual_crating_required,
      formatFn: (value: any) => (value === "-" ? "-" : value ? "Yes" : "No"),
      requestedValue: requested_crating_required,
      actualValue: actual_crating_required,
    },
    {
      name: intl.formatMessage({ id: "new-request.packing-list.NMFC", defaultMessage: "NMFC Class" }),
      shouldRender: requested_nmfc_class || actual_nmfc_class,
      formatFn: (value: any) => value,
      requestedValue: requested_nmfc_class,
      actualValue: actual_nmfc_class,
    },
    {
      name: intl.formatMessage({
        id: "quote-request.costDetailAdditionalInfomation",
        defaultMessage: "Additional information",
      }),
      shouldRender: requested_additional_information || actual_additional_information,
      formatFn: (value: any) => value || "-",
      requestedValue: requested_additional_information,
      actualValue: actual_additional_information,
    },
  ];

  // Generate rows for customs information
  // Create a map of requested customs objects based on their IDs
  const requestedCustomsMap = requested_good_customs_information?.reduce((acc: any, customs: any) => {
    acc[customs.id] = customs;
    return acc;
  }, {});

  // Create a map of actual customs objects based on their IDs
  const actualCustomsMap = actual_good_customs_information?.reduce((acc: any, customs: any, index: number) => {
    // If the actual customs object doesn't have an ID, generate a unique ID based on the index
    const id = customs.id || `temp_${index}`;
    acc[id] = customs;
    return acc;
  }, {});

  /**
   * If the commodity has a parent ID, then we cannot map requested and actual customs based on their IDs
   * because the updated item (the child) will have a different ID from the original item (the parent).
   * Instead, we will merge them based on their index, same as the old ui.
   */
  let mergedCustoms: any = [];
  if (actual_parent_id) {
    // Merge based on their index
    mergedCustoms = actual_good_customs_information.map((actualCustoms: any, index: number) => ({
      requestedCustoms: requested_good_customs_information?.[index] || {},
      actualCustoms,
    }));
    // Add any remaining requested customs info that doesn't have a corresponding actual customs info
    if (requested_good_customs_information?.length > actual_good_customs_information.length) {
      mergedCustoms = [
        ...mergedCustoms,
        ...requested_good_customs_information.slice(actual_good_customs_information.length).map((requestedCustoms: any) => ({
          requestedCustoms,
          actualCustoms: {},
        })),
      ];
    }
  } else {
    // Merge based on their IDs
    mergedCustoms = Object.keys({ ...requestedCustomsMap, ...actualCustomsMap }).map((id) => ({
      requestedCustoms: requestedCustomsMap?.[id] || {},
      actualCustoms: actualCustomsMap?.[id] || {},
    }));
  }

  const customsRows = mergedCustoms
    .map(({ requestedCustoms, actualCustoms }: any, index: number) => [
      {
        name: `${intl.formatMessage({
          id: "new-request.quote-confirmation.packing-list.customInformation",
          defaultMessage: "Customs information",
        })} ${index + 1}`,
        shouldRender: true,
        formatFn: (value: any) => value,
        requestedValue: "",
        actualValue: "",
      },
      {
        name: intl.formatMessage({
          id: "new-request.packing-list.commodityInformation",
          defaultMessage: "Commodity information",
        }),
        shouldRender: true,
        isCustomsInfo: true,
        formatFn: (value: any) => value,
        requestedValue: requestedCustoms.description || "-",
        actualValue: actualCustoms.description || "-",
      },
      {
        name: intl.formatMessage({ id: "new-request.packing-list.hsCode", defaultMessage: "HS Code" }),
        shouldRender: true,
        isCustomsInfo: true,
        formatFn: (value: any) => value,
        requestedValue: requestedCustoms.hs_code || "-",
        actualValue: actualCustoms.hs_code || "-",
      },
      {
        name: intl.formatMessage({ id: "new-request.packing-list.customCommodityValue", defaultMessage: "Value" }),
        shouldRender: true,
        isCustomsInfo: true,
        formatFn: (value: any) => value,
        requestedValue: `${requestedCustoms.value || "-"} ${requestedCustoms.currency_code || "-"}`,
        actualValue: `${actualCustoms.value || "-"} ${
          actualCustoms.currency_code && actualCustoms.value ? actualCustoms.currency_code : ""
        }`,
      },
    ])
    .flat();

  // Concatenate existing rows with customs rows
  const rows = [
    ...defaultRows, // Use the existing rows defined earlier in the function
    ...customsRows,
  ];

  // Replace undefined values with "-"
  rows.forEach((row) => {
    if (typeof row.requestedValue === "undefined" || String(row.requestedValue).includes("undefined"))
      row.requestedValue = "-";
    if (typeof row.actualValue === "undefined" || String(row.actualValue).includes("undefined")) row.actualValue = "-";
  });

  return rows;
};

export default generateRows;
